/**
 * Listado de productos del Centro de Inventario.
 * Tabla densa con TanStack Table + filtros + paginacion server-side.
 */
import { useEffect, useMemo, useRef, useState, type ChangeEvent } from 'react';
import { createPortal } from 'react-dom';
import { Link, useNavigate, createFileRoute } from '@tanstack/react-router';
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
  type SortingState,
} from '@tanstack/react-table';
import { ChevronDown, Download, Eye, Package, Search, SlidersHorizontal, X } from 'lucide-react';

import { PageLayout } from '@/components/layout/PageLayout';
import { Card, CardContent } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { EmptyState } from '@/components/ui/EmptyState';
import { Skeleton } from '@/components/ui/Skeleton';
import { Checkbox } from '@/components/ui/Checkbox';
import { Can } from '@/components/permissions/Can';
import { PERMISSIONS } from '@/permissions/constants';
import { useSessionStore } from '@/stores/session';
import { formatMoney, formatCost } from '@/lib/money';
import { cn } from '@/lib/cn';

import { usePriceLists, useProducts } from '@/features/inventory-center/api';
import { useWarehouses } from '@/features/inventory-center/api';
import { useAlertsSummary } from '@/features/inventory-center/api';
import { useCurrentExchangeRatesForPos } from '@/features/pos/api';
import { useUiPreferences, useUpdateUiPreferences } from '@/features/company-settings/api';
import { CreateProductDialog } from '@/features/inventory-center/dialogs/CreateProductDialog';
import { CustomizeInventoryColumnsDialog } from '@/features/inventory-center/dialogs/CustomizeInventoryColumnsDialog';
import { ExportInventoryDialog } from '@/features/inventory-center/dialogs/ExportInventoryDialog';
import {
  DEFAULT_INVENTORY_TABLE_COLUMNS,
  getStoredInventoryColumnsVisibility,
  resetStoredInventoryColumnsVisibility,
  saveStoredInventoryColumnsVisibility,
  type InventoryTableColumnsVisibility,
} from '@/features/inventory-center/inventoryColumnsConfig';
import { BulkActionsMenu } from '@/features/inventory-center/bulk-actions/BulkActionsMenu';
import { useExportProducts } from '@/features/inventory-center/useExportProducts';
import type { PriceList, Product } from '@/features/inventory-center/schemas';

type TrackingFilter = 'all' | 'quantity' | 'serialized';
type StockFilter = 'all' | 'available' | 'low' | 'critical' | 'out' | 'overstock';
type StatusFilter = 'all' | 'active' | 'inactive';

interface InventorySearch {
  search: string;
  tracking: TrackingFilter;
  stock: StockFilter;
  status: StatusFilter;
  page: number;
  brand_id: number | undefined;
  category_id: number | undefined;
  tag_id: number | undefined;
  warehouse_id: number | undefined;
  low_stock_threshold: number | undefined;
  sort_by: string | undefined;
  sort_dir: 'asc' | 'desc' | undefined;
}

export const Route = createFileRoute('/_authed/inventory/')({
  component: InventoryListPage,
  validateSearch: (search: Record<string, unknown>): InventorySearch => ({
    search: typeof search.search === 'string' ? search.search : '',
    tracking: ['all', 'quantity', 'serialized'].includes(search.tracking as string)
      ? (search.tracking as TrackingFilter)
      : 'all',
    stock: ['all', 'available', 'low', 'critical', 'out', 'overstock'].includes(
      search.stock as string,
    )
      ? (search.stock as StockFilter)
      : 'all',
    status: ['all', 'active', 'inactive'].includes(search.status as string)
      ? (search.status as StatusFilter)
      : 'all',
    page: typeof search.page === 'number' ? search.page : 1,
    brand_id:
      typeof search.brand_id === 'number'
        ? search.brand_id
        : typeof search.brand_id === 'string'
          ? Number(search.brand_id) || undefined
          : undefined,
    category_id:
      typeof search.category_id === 'number'
        ? search.category_id
        : typeof search.category_id === 'string'
          ? Number(search.category_id) || undefined
          : undefined,
    tag_id:
      typeof search.tag_id === 'number'
        ? search.tag_id
        : typeof search.tag_id === 'string'
          ? Number(search.tag_id) || undefined
          : undefined,
    warehouse_id:
      typeof search.warehouse_id === 'number'
        ? search.warehouse_id
        : typeof search.warehouse_id === 'string'
          ? Number(search.warehouse_id) || undefined
          : undefined,
    low_stock_threshold:
      typeof search.low_stock_threshold === 'number'
        ? search.low_stock_threshold
        : typeof search.low_stock_threshold === 'string'
          ? Number(search.low_stock_threshold) || undefined
          : undefined,
    sort_by: typeof search.sort_by === 'string' ? search.sort_by : undefined,
    sort_dir: search.sort_dir === 'asc' || search.sort_dir === 'desc' ? search.sort_dir : undefined,
  }),
});

function InventoryListPage() {
  const search: InventorySearch = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });

  const [searchInput, setSearchInput] = useState(search.search);
  const [createOpen, setCreateOpen] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());

  useEffect(() => {
    setSearchInput(search.search);
  }, [search.search]);

  useEffect(() => {
    if (searchInput === search.search) return;
    const timer = setTimeout(() => {
      void navigate({ search: { ...search, search: searchInput, page: 1 } });
    }, 250);
    return () => clearTimeout(timer);
  }, [searchInput, search, navigate]);

  const filters = useMemo(
    () => ({
      search: search.search,
      tracking_type: search.tracking,
      stock_status: search.stock,
      active_status: search.status,
      warehouse_id: search.warehouse_id,
      page: search.page,
      per_page: 25,
      with_prices: 1 as const,
    }),
    [search.search, search.tracking, search.stock, search.status, search.warehouse_id, search.page],
  );

  const { data, isLoading, isError } = useProducts(filters);
  const { data: alerts } = useAlertsSummary();
  const permissions = useSessionStore((state) => state.permissions);
  const canViewCurrency = permissions?.has(PERMISSIONS.CURRENCY_VIEW) ?? false;
  const { data: rates = [] } = useCurrentExchangeRatesForPos({ enabled: canViewCurrency });
  const activeRate = rates.find((rate) => rate.is_active !== false) ?? rates[0] ?? null;
  const { data: warehouses = [] } = useWarehouses();
  const { data: priceLists = [] } = usePriceLists(false);
  const exportProducts = useExportProducts();

  const updateSearch = (patch: Partial<InventorySearch>) => {
    void navigate({ search: { ...search, search: searchInput, ...patch, page: 1 } });
  };

  const goToPage = (page: number) => {
    void navigate({ search: { ...search, page } });
  };

  const tenantId = useSessionStore((state) => state.tenant?.id);
  const { data: uiPreferences } = useUiPreferences();
  const updateUiPreferences = useUpdateUiPreferences();
  const [columnsVisibility, setColumnsVisibility] = useState<InventoryTableColumnsVisibility>(() =>
    getStoredInventoryColumnsVisibility(tenantId),
  );
  const [customizeColumnsOpen, setCustomizeColumnsOpen] = useState(false);
  const [exportDialogOpen, setExportDialogOpen] = useState(false);

  useEffect(() => {
    if (uiPreferences?.inventory_table_columns) {
      const merged = {
        ...DEFAULT_INVENTORY_TABLE_COLUMNS,
        ...uiPreferences.inventory_table_columns,
        name: true,
      };
      saveStoredInventoryColumnsVisibility(merged, tenantId);
      setColumnsVisibility(merged);
    }
  }, [uiPreferences?.inventory_table_columns, tenantId]);

  const handleColumnsVisibilityChange = (newVisibility: InventoryTableColumnsVisibility) => {
    setColumnsVisibility(newVisibility);
    saveStoredInventoryColumnsVisibility(newVisibility, tenantId);
    updateUiPreferences.mutate({
      ...uiPreferences,
      inventory_table_columns: newVisibility,
    });
  };

  const handleResetColumnsVisibility = () => {
    const reset = resetStoredInventoryColumnsVisibility(tenantId);
    setColumnsVisibility(reset);
    updateUiPreferences.mutate({
      ...uiPreferences,
      inventory_table_columns: reset,
    });
  };

  const columns = useColumns(
    selectedIds,
    setSelectedIds,
    data?.data ?? [],
    search,
    activeRate?.rate ?? null,
    priceLists,
    columnsVisibility,
  );

  const table = useReactTable({
    data: data?.data ?? [],
    columns,
    state: { sorting: [] as SortingState },
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
    manualFiltering: true,
    manualSorting: true,
    pageCount: data?.meta.last_page ?? 0,
  });

  return (
    <PageLayout
      title="Centro de Inventario"
      description="Listado de productos con stock, precios y estado."
      actions={
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            leftIcon={<SlidersHorizontal className="size-4" />}
            onClick={() => setCustomizeColumnsOpen(true)}
            data-testid="customize-columns-btn"
          >
            Columnas
          </Button>
          <Button
            variant="outline"
            size="sm"
            leftIcon={<Download className="size-4" />}
            onClick={() => setExportDialogOpen(true)}
            loading={exportProducts.isExporting}
            data-testid="export-csv"
          >
            Exportar CSV
          </Button>
          <Can I={PERMISSIONS.PRODUCTS_CREATE}>
            <Button onClick={() => setCreateOpen(true)} data-testid="new-product">
              + Nuevo producto
            </Button>
          </Can>
        </div>
      }
    >
      <InventoryKpis total={data?.meta.total ?? 0} alerts={alerts} />

      <BulkActionsMenu
        selectedIds={Array.from(selectedIds)}
        onClearSelection={() => setSelectedIds(new Set())}
        onSuccess={() => setSelectedIds(new Set())}
      />

      {/* Filtros */}
      <Card>
        <CardContent className="grid grid-cols-1 gap-3 p-4 sm:grid-cols-2 lg:grid-cols-6">
          <div className="relative sm:col-span-2 lg:col-span-2">
            <Search
              className="text-text-muted pointer-events-none absolute top-1/2 left-2.5 size-4 -translate-y-1/2"
              aria-hidden="true"
            />
            <Input
              value={searchInput}
              onChange={(e: ChangeEvent<HTMLInputElement>) => setSearchInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') updateSearch({ search: searchInput });
              }}
              placeholder="Buscar por SKU o nombre..."
              className={cn('pl-8', searchInput && 'pr-8')}
              data-testid="inventory-search"
            />
            {searchInput ? (
              <button
                type="button"
                onClick={() => {
                  setSearchInput('');
                  void navigate({ search: { ...search, search: '', page: 1 } });
                }}
                className="text-text-muted hover:text-text-primary absolute top-1/2 right-2.5 -translate-y-1/2 rounded p-0.5 transition-colors"
                aria-label="Limpiar búsqueda"
              >
                <X className="size-4" />
              </button>
            ) : null}
          </div>
          <select
            className={selectClass}
            value={search.tracking}
            onChange={(e: ChangeEvent<HTMLSelectElement>) =>
              updateSearch({ tracking: e.target.value as TrackingFilter })
            }
            data-testid="inventory-tracking"
          >
            <option value="all">Todos los tipos</option>
            <option value="quantity">Por cantidad</option>
            <option value="serialized">Serializados</option>
          </select>
          <select
            className={selectClass}
            value={search.stock}
            onChange={(e: ChangeEvent<HTMLSelectElement>) =>
              updateSearch({ stock: e.target.value as StockFilter })
            }
          >
            <option value="all">Todos los stocks</option>
            <option value="available">Con stock</option>
            <option value="low">Bajo stock</option>
            <option value="critical">Critico</option>
            <option value="out">Sin stock</option>
            <option value="overstock">Sobre stock</option>
          </select>
          <select
            className={selectClass}
            value={search.warehouse_id ? String(search.warehouse_id) : ''}
            onChange={(e: ChangeEvent<HTMLSelectElement>) =>
              updateSearch({ warehouse_id: e.target.value ? Number(e.target.value) : undefined })
            }
            data-testid="inventory-warehouse"
          >
            <option value="">Todos los almacenes</option>
            {warehouses.map((w) => (
              <option key={w.id} value={String(w.id)}>
                {w.code}
              </option>
            ))}
          </select>
          <select
            className={selectClass}
            value={search.status}
            onChange={(e: ChangeEvent<HTMLSelectElement>) =>
              updateSearch({ status: e.target.value as StatusFilter })
            }
          >
            <option value="all">Todos los estados</option>
            <option value="active">Activos</option>
            <option value="inactive">Inactivos</option>
          </select>
        </CardContent>
      </Card>

      {/* Tabla */}
      <Card>
        <CardContent className="p-0">
          {isLoading && <TableSkeleton />}
          {isError && (
            <EmptyState
              title="No se pudo cargar el inventario"
              description="Verifica tu conexión o tus permisos."
            />
          )}
          {data && data.data.length === 0 && (
            <EmptyState
              icon={<Package className="size-8" />}
              title="Sin productos"
              description="No hay productos que coincidan con los filtros aplicados."
            />
          )}
          {data && data.data.length > 0 && (
            <div className="overflow-x-auto">
              <table className="table-dense w-full">
                <thead className="border-border bg-bg/60 border-b text-left">
                  {table.getHeaderGroups().map((hg) => (
                    <tr key={hg.id}>
                      {hg.headers.map((header) => (
                        <th
                          key={header.id}
                          className="text-text-secondary px-3 py-2 font-semibold tracking-wide uppercase"
                        >
                          {header.isPlaceholder
                            ? null
                            : flexRender(header.column.columnDef.header, header.getContext())}
                        </th>
                      ))}
                    </tr>
                  ))}
                </thead>
                <tbody>
                  {table.getRowModel().rows.map((row) => (
                    <tr
                      key={row.id}
                      className="border-border hover:bg-bg/50 border-b last:border-b-0"
                    >
                      {row.getVisibleCells().map((cell) => (
                        <td key={cell.id} className="px-3 py-2">
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Paginacion */}
      {data && data.meta.last_page > 1 && (
        <div className="flex items-center justify-between text-sm">
          <p className="text-text-muted">
            Mostrando {data.data.length} de {data.meta.total} productos
          </p>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={filters.page <= 1}
              onClick={() => goToPage(Math.max(1, filters.page - 1))}
            >
              Anterior
            </Button>
            <span className="text-text-muted flex items-center px-2">
              Pagina {data.meta.current_page} / {data.meta.last_page}
            </span>
            <Button
              variant="outline"
              size="sm"
              disabled={filters.page >= data.meta.last_page}
              onClick={() => goToPage(filters.page + 1)}
            >
              Siguiente
            </Button>
          </div>
        </div>
      )}

      <CreateProductDialog open={createOpen} onOpenChange={setCreateOpen} />
      <CustomizeInventoryColumnsDialog
        open={customizeColumnsOpen}
        onOpenChange={setCustomizeColumnsOpen}
        visibility={columnsVisibility}
        onChange={handleColumnsVisibilityChange}
        onReset={handleResetColumnsVisibility}
      />
      <ExportInventoryDialog
        open={exportDialogOpen}
        onOpenChange={setExportDialogOpen}
        filters={filters}
        exportProducts={exportProducts}
      />
    </PageLayout>
  );
}

const selectClass = cn(
  'flex h-9 w-full rounded border border-border-strong bg-surface px-3 text-sm shadow-sm',
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1',
);

function useColumns(
  selectedIds: Set<number>,
  setSelectedIds: React.Dispatch<React.SetStateAction<Set<number>>>,
  data: Product[],
  search: InventorySearch,
  activeRate: number | null,
  priceLists: PriceList[],
  columnsVisibility: InventoryTableColumnsVisibility,
) {
  const columnHelper = createColumnHelper<Product>();

  return useMemo(() => {
    const selectCol = columnHelper.display({
      id: 'select',
      header: () => (
        <Checkbox
          checked={selectedIds.size > 0 && selectedIds.size === data.length}
          onCheckedChange={(checked) => {
            if (checked) {
              setSelectedIds(new Set(data.map((p: Product) => p.id)));
            } else {
              setSelectedIds(new Set());
            }
          }}
          aria-label="Seleccionar todo"
        />
      ),
      cell: (info) => (
        <Checkbox
          checked={selectedIds.has(info.row.original.id)}
          onCheckedChange={(checked) => {
            setSelectedIds((prev) => {
              const next = new Set(prev);
              if (checked) next.add(info.row.original.id);
              else next.delete(info.row.original.id);
              return next;
            });
          }}
          aria-label={`Seleccionar ${info.row.original.name}`}
          data-testid={`select-${info.row.original.id}`}
        />
      ),
    });

    const imageCol = columnHelper.display({
      id: 'image',
      header: () => null,
      cell: (info) => {
        const row = info.row.original;
        const src = row.primary_image_url ?? row.images?.[0]?.thumb_url ?? row.image_url;
        if (!src) {
          return (
            <div className="flex size-10 items-center justify-center rounded border border-border bg-bg text-[10px] text-text-muted">
              —
            </div>
          );
        }
        return (
          <div className="size-10 overflow-hidden rounded border border-border bg-bg">
            <img
              src={src}
              alt={row.name}
              className="size-full object-cover"
              loading="lazy"
              onError={(e) => {
                e.currentTarget.style.display = 'none';
              }}
            />
          </div>
        );
      },
    });

    const skuCol = columnHelper.accessor('sku', {
      id: 'sku',
      header: 'SKU',
      cell: (info) => (
        <code className="bg-bg rounded px-1.5 py-0.5 text-xs">{info.getValue() || '—'}</code>
      ),
    });

    const barcodeCol = columnHelper.accessor('barcode', {
      id: 'barcode',
      header: 'Código',
      cell: (info) => {
        const val = info.getValue();
        return val ? (
          <code className="bg-bg rounded px-1.5 py-0.5 text-xs font-mono text-text-primary">
            {val}
          </code>
        ) : (
          <span className="text-text-muted text-xs">—</span>
        );
      },
    });

    const nameCol = columnHelper.accessor('name', {
      id: 'name',
      header: 'Nombre',
      cell: (info) => {
        const row = info.row.original;
        const isCopy = !row.is_catalog_master && row.catalog_product_id != null;
        const masterActive = row.is_catalog_active !== false;
        return (
          <div className="flex items-center gap-2">
            <span className="text-text-primary font-medium">{info.getValue()}</span>
            {isCopy && (
              <Badge
                variant="info"
                className="font-normal"
                data-testid={`shared-badge-${row.id}`}
              >
                Compartido
              </Badge>
            )}
            {row.is_catalog_master && (
              <Badge
                variant="warning"
                className="font-normal"
                data-testid={`master-badge-${row.id}`}
              >
                Maestro
              </Badge>
            )}
            {!masterActive && (
              <Badge variant="default" className="font-normal">
                Inactivo en grupo
              </Badge>
            )}
          </div>
        );
      },
    });

    const brandCol = columnHelper.accessor((row) => row.brand?.name ?? '', {
      id: 'brand',
      header: 'Marca',
      cell: (info) => {
        const val = info.getValue();
        return val ? (
          <span className="text-text-primary text-xs">{val}</span>
        ) : (
          <span className="text-text-muted text-xs">—</span>
        );
      },
    });

    const categoriesCol = columnHelper.accessor(
      (row) => row.categories?.map((c) => c.name).join(', ') ?? '',
      {
        id: 'categories',
        header: 'Categorías',
        cell: (info) => {
          const val = info.getValue();
          return val ? (
            <span
              className="text-text-primary text-xs truncate max-w-[150px] inline-block"
              title={val}
            >
              {val}
            </span>
          ) : (
            <span className="text-text-muted text-xs">—</span>
          );
        },
      },
    );

    const trackingTypeCol = columnHelper.accessor('tracking_type', {
      id: 'tracking_type',
      header: 'Tipo',
      cell: (info) => {
        const t = info.getValue();
        return (
          <Badge variant={t === 'serialized' ? 'info' : 'default'}>
            {t === 'serialized' ? 'Serializado' : 'Cantidad'}
          </Badge>
        );
      },
    });

    const stockCol = columnHelper.accessor(
      (row) => {
        // Suma de stock_balances.quantity_available. Si el listado
        // se filtro por warehouse_id, este campo refleja SOLO ese
        // almacen. Si el producto no tiene stock en ningun almacen,
        // el backend retorna 0 y mostramos "Sin stock".
        const v = row.available_stock;
        if (v == null) return '0';
        const n = typeof v === 'string' ? parseFloat(v) : v;
        return Number.isNaN(n) ? '0' : String(n);
      },
      {
        id: 'stock',
        header: () => (
          <span>
            Stock
            {search.warehouse_id && (
              <span className="text-text-muted ml-1 text-[10px] font-normal">(filtrado)</span>
            )}
          </span>
        ),
        cell: (info) => {
          const v = info.row.original.available_stock;
          const n = v == null ? 0 : typeof v === 'string' ? parseFloat(v) : v;
          return (
            <span className={cn('tabular-nums', n <= 0 && 'text-text-muted')}>
              {Number.isFinite(n) ? n.toFixed(0) : '0'}
            </span>
          );
        },
      },
    );

    const costPriceCol = columnHelper.accessor(
      (row) => row.last_purchase_cost ?? row.average_cost ?? null,
      {
        id: 'cost_price',
        header: 'Precio costo',
        cell: (info) => {
          const val = info.getValue();
          return val != null ? (
            <span className="text-text-primary text-xs font-mono font-medium">
              {formatCost(val)}
            </span>
          ) : (
            <span className="text-text-muted text-xs">—</span>
          );
        },
      },
    );

    const basePriceCol = columnHelper.accessor('base_price', {
      id: 'base_price',
      header: 'Precio base',
      cell: (info) => formatMoney(info.getValue()),
    });

    const profitCol = columnHelper.accessor(
      (row) => {
        const cost = row.last_purchase_cost ?? row.average_cost ?? null;
        const price = Number(row.base_price ?? 0);
        if (cost == null || isNaN(Number(cost)) || price <= 0) return null;
        const profit = price - Number(cost);
        const marginPct = (profit / price) * 100;
        return { profit, marginPct };
      },
      {
        id: 'profit',
        header: 'Ganancia',
        cell: (info) => {
          const val = info.getValue();
          if (!val) return <span className="text-text-muted text-xs">—</span>;
          const isPositive = val.profit >= 0;
          return (
            <div className="flex flex-col">
              <span
                className={cn(
                  'text-xs font-mono font-bold',
                  isPositive ? 'text-emerald-600' : 'text-rose-600',
                )}
              >
                {formatMoney(val.profit)}
              </span>
              <span className="text-[10px] text-text-muted font-mono">
                {val.marginPct.toFixed(1)}%
              </span>
            </div>
          );
        },
      },
    );

    const priceListCol = columnHelper.display({
      id: 'price_list',
      header: 'Lista predeterminada',
      cell: (info) => (
        <PriceListCell
          product={info.row.original}
          activeRate={activeRate}
          priceLists={priceLists}
        />
      ),
    });

    const isActiveCol = columnHelper.accessor('is_active', {
      id: 'is_active',
      header: 'Estado',
      cell: (info) => (
        <Badge variant={info.getValue() ? 'success' : 'default'}>
          {info.getValue() ? 'Activo' : 'Inactivo'}
        </Badge>
      ),
    });

    const actionsCol = columnHelper.display({
      id: 'actions',
      header: '',
      cell: (info) => (
        <Link
          to="/inventory/$productId"
          params={{ productId: String(info.row.original.id) }}
          className="text-primary inline-flex items-center gap-1 text-sm font-medium hover:underline"
          data-testid={`inventory-view-${info.row.original.id}`}
        >
          <Eye className="size-3.5" aria-hidden="true" />
          Ver
        </Link>
      ),
    });

    return [
      selectCol,
      ...(columnsVisibility.image ? [imageCol] : []),
      ...(columnsVisibility.sku ? [skuCol] : []),
      ...(columnsVisibility.barcode ? [barcodeCol] : []),
      nameCol,
      ...(columnsVisibility.brand ? [brandCol] : []),
      ...(columnsVisibility.categories ? [categoriesCol] : []),
      ...(columnsVisibility.tracking_type ? [trackingTypeCol] : []),
      ...(columnsVisibility.stock ? [stockCol] : []),
      ...(columnsVisibility.cost_price ? [costPriceCol] : []),
      ...(columnsVisibility.base_price ? [basePriceCol] : []),
      ...(columnsVisibility.profit ? [profitCol] : []),
      ...(columnsVisibility.price_list ? [priceListCol] : []),
      ...(columnsVisibility.is_active ? [isActiveCol] : []),
      actionsCol,
    ];
  }, [
    columnHelper,
    selectedIds,
    setSelectedIds,
    data,
    search.warehouse_id,
    activeRate,
    priceLists,
    columnsVisibility,
  ]);
}

function InventoryKpis({
  total,
  alerts,
}: {
  total: number;
  alerts?: { out_count: number; low_count: number };
}) {
  return (
    <div className="grid gap-4 grid-cols-1 sm:grid-cols-3">
      <KpiCard label="Productos encontrados" value={total.toLocaleString('es-VE')} />
      <KpiCard label="Sin stock" value={String(alerts?.out_count ?? '-')} tone="danger" />
      <KpiCard label="Stock bajo" value={String(alerts?.low_count ?? '-')} tone="warning" />
    </div>
  );
}

function KpiCard({
  label,
  value,
  tone = 'default',
}: {
  label: string;
  value: string;
  tone?: 'default' | 'danger' | 'warning';
}) {
  return (
    <Card className="hover:shadow-md transition-shadow">
      <CardContent className="flex items-center justify-between p-4 sm:p-5">
        <div>
          <span className="text-slate-400 text-xs font-bold tracking-wider uppercase block">{label}</span>
          <strong
            className={cn(
              'text-2xl sm:text-3xl font-black font-mono tabular-nums tracking-tight mt-1 block',
              tone === 'danger' && 'text-rose-600',
              tone === 'warning' && 'text-amber-600',
              tone === 'default' && 'text-slate-900',
            )}
          >
            {value}
          </strong>
        </div>
      </CardContent>
    </Card>
  );
}

function PriceListCell({
  product,
  activeRate,
  priceLists,
}: {
  product: Product;
  activeRate: number | null;
  priceLists: PriceList[];
}) {
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const popoverRef = useRef<HTMLDivElement | null>(null);
  const manualPrices = (product.prices ?? []).filter((price) => price.is_active !== false);
  const manualListIds = new Set(manualPrices.map((price) => price.price_list_id));
  const manualById = new Map(manualPrices.map((price) => [price.price_list_id, price]));

  // Precio efectivo de una lista respetando la cadena de lista base
  // (ej. CASHEA = DETAL + 16%), con guard de ciclos.
  const effectivePrice = (
    list: PriceList,
    seen = new Set<number>(),
  ): number | null => {
    if (seen.has(list.id)) return product.base_price != null ? Number(product.base_price) : null;
    seen.add(list.id);

    const manual = manualById.get(list.id);
    if (manual) return Number(manual.price);

    let base: number | null = product.base_price != null ? Number(product.base_price) : null;
    if (list.base_price_list_id) {
      const baseList = priceLists.find((l) => l.id === list.base_price_list_id);
      if (baseList) base = effectivePrice(baseList, seen);
    }

    if (base == null) return null;
    const markup = Number(list.markup_percentage ?? 0);
    return Number((base * (1 + markup / 100)).toFixed(2));
  };

  const automaticPrices = priceLists
    .filter(
      (list) =>
        list.is_active &&
        !manualListIds.has(list.id) &&
        (list.markup_percentage != null || list.base_price_list_id != null),
    )
    .flatMap((list) => {
      const computed = effectivePrice(list);
      if (computed == null) return [];
      return [
        {
          id: -list.id,
          price_list_id: list.id,
          price_list: {
            id: list.id,
            name: list.name,
            code: list.code,
            is_default: Boolean(list.is_default),
            is_active: list.is_active,
          },
          price: computed,
          currency: 'USD',
          is_active: true,
          automatic: true,
        },
      ];
    });
  const prices = [...manualPrices, ...automaticPrices];
  const defaultPrice = prices.find((price) => price.price_list?.is_default) ?? prices[0];
  const [expanded, setExpanded] = useState(false);
  const [popoverPosition, setPopoverPosition] = useState<{ top: number; left: number } | null>(
    null,
  );

  useEffect(() => {
    if (!expanded) return;

    function closeOnOutside(event: PointerEvent): void {
      const target = event.target as Node;
      if (!triggerRef.current?.contains(target) && !popoverRef.current?.contains(target)) {
        setExpanded(false);
      }
    }

    function closeOnEscape(event: KeyboardEvent): void {
      if (event.key === 'Escape') setExpanded(false);
    }

    function closeOnScroll(): void {
      setExpanded(false);
    }

    document.addEventListener('pointerdown', closeOnOutside);
    document.addEventListener('keydown', closeOnEscape);
    window.addEventListener('scroll', closeOnScroll, true);
    return () => {
      document.removeEventListener('pointerdown', closeOnOutside);
      document.removeEventListener('keydown', closeOnEscape);
      window.removeEventListener('scroll', closeOnScroll, true);
    };
  }, [expanded]);

  if (!defaultPrice) return <span className="text-text-muted">Sin lista</span>;

  const displayLocal = (price: number, currency: string) => {
    if (currency === 'VES')
      return `Bs ${price.toLocaleString('es-VE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    if (!activeRate) return null;
    return `Bs ${(price * activeRate).toLocaleString('es-VE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  function togglePopover(): void {
    if (expanded) {
      setExpanded(false);
      return;
    }

    const rect = triggerRef.current?.getBoundingClientRect();
    if (!rect) return;
    const width = 256;
    setPopoverPosition({
      top: Math.min(rect.bottom + 8, window.innerHeight - 220),
      left: Math.min(Math.max(8, rect.right - width), window.innerWidth - width - 8),
    });
    setExpanded(true);
  }

  return (
    <div className="relative min-w-36">
      <button
        ref={triggerRef}
        type="button"
        className="hover:text-primary flex items-center gap-1 text-left"
        onClick={togglePopover}
      >
        <span>
          <span className="text-text-muted block text-xs">
            {defaultPrice.price_list?.name ?? 'Lista'}
          </span>
          <strong>
            {defaultPrice.currency === 'VES'
              ? `Bs ${defaultPrice.price.toFixed(2)}`
              : `$${defaultPrice.price.toFixed(2)}`}
          </strong>
        </span>
        {prices.length > 1 && (
          <span className="bg-bg text-text-muted rounded-full px-1.5 text-[10px]">
            +{prices.length - 1}
          </span>
        )}
        <ChevronDown
          className={cn('text-text-muted size-3 transition-transform', expanded && 'rotate-180')}
        />
      </button>
      {expanded &&
        popoverPosition &&
        createPortal(
          <div
            ref={popoverRef}
            className="border-border bg-surface fixed z-[100] w-64 rounded-lg border p-2 shadow-xl"
            style={{ top: popoverPosition.top, left: popoverPosition.left }}
          >
            <p className="text-text-muted mb-1 px-1 text-[10px] font-semibold tracking-wide uppercase">
              Precios por lista
            </p>
            {prices.map((price) => (
              <div
                key={price.id}
                className="hover:bg-bg flex items-center justify-between gap-2 rounded px-1 py-1.5 text-xs"
              >
                <span className="truncate">{price.price_list?.name ?? 'Lista'}</span>
                <span className="text-right font-semibold">
                  {price.currency === 'VES'
                    ? `Bs ${price.price.toFixed(2)}`
                    : `$${price.price.toFixed(2)}`}
                  {displayLocal(price.price, price.currency) && price.currency !== 'VES' && (
                    <small className="text-text-muted ml-1 block font-normal">
                      {displayLocal(price.price, price.currency)}
                    </small>
                  )}
                </span>
              </div>
            ))}
            {activeRate && (
              <p className="text-text-muted mt-1 px-1 text-[10px]">
                VES calculado con tasa activa {activeRate}.
              </p>
            )}
          </div>,
          document.body,
        )}
    </div>
  );
}

function TableSkeleton() {
  return (
    <div className="space-y-2 p-4">
      {Array.from({ length: 6 }).map((_, i) => (
        <Skeleton key={i} className="h-8 w-full" />
      ))}
    </div>
  );
}
